import React, {lazy, createRef} from "react";
import {History} from "history";
import {connect} from "react-redux";
import {Route, Router, Switch} from "react-router-dom";
import DelagetFooter from "./components/DelagetFooter";
import DelagetHeader from "./components/DelagetHeader";
import FreemiumMarketingModal from "./components/FreemiumMarketingModal";
import {GlobalTicketModalManager} from "./features/ticket";
import {LoadingMessage} from "@/components/SystemMessages";
import configurationApi from "./features/configuration/api/configurationAPI";
import {IRootGroupInvalidLocations} from "./features/configuration/api/configurationModels";
import GroupTreeValidationError from "./features/configuration/components/GroupTreeValidationError";
import BannerMessage from "./features/configuration/components/BannerMessage";
import Dashboard from "./features/dashboard/Dashboard";
import BentoLoadingScreen from "./features/bento/components/BentoLoadingScreen";
import BentoShell from "./features/bento/components/BentoShell";
import {AppState} from "@/state/rootReducer";
import {throttle, DebouncedFunc} from "lodash";
import {RouteType} from "./util/appParamsUtil";
import ProtectedRoute from "@/features/authentication/components/ProtectedRoute";
import {actionCreators} from "@/features/application/state/actions";
import Chat from "./coach-ai/src/pages/chat/Chat";
import {ENV} from "@/configuration";
import hj from "./tracking/hotjar";

//Load Route Components Asynchronously to speed up initial page load
const AsyncAdminSettings = lazy(
    () => import("./features/application/AdminSettings")
);
const AsyncUserSettings = lazy(() => import("./features/application/UserSettings"));
const AsyncLocationMetricThresholdConfigurationContainer = lazy(
    () =>
        import(
            "./features/application/components/LocationThresholdSettings/LocationMetricThresholdConfigurationContainer"
        )
);
const AsyncMetricDetails = lazy(
    () => import("./features/metricDetails/MetricDetails")
);
const AsyncTasks = lazy(() => import("./features/tasks/Tasks"));
const AsyncTaskDetails = lazy(() => import("./features/tasks/TaskDetails"));
const AsyncMonitors = lazy(() => import("./features/monitors/Monitors"));
const AsyncMonitorSearch = lazy(() => import("./features/monitors/MonitorSearch"));
const AsyncCreateMonitor = lazy(() => import("./features/monitors/CreateMonitor"));
const AsyncMonitorDetails = lazy(
    () => import("./features/monitors/MonitorDetailsContainer")
);
const AsyncDeliveryProvider = lazy(
    () => import("./features/deliveryProvider/DeliveryProvider")
);
const AsyncEmployeeRetention = lazy(
    () => import("./features/homeOffice/HomeOffice")
);
const AsyncDifferentialPay = lazy(
    () => import("./features/DifferentialPay/DifferentialPay")
);
const AsyncDeliveryOperations = lazy(
    () => import("./features/deliveryOperations/DeliveryOperations")
);
const AsyncTheme = lazy(() => import("./components/Theme"));
const AsyncA2HS = lazy(() => import("./features/application/A2HSInstructions"));
const AsyncPageConfiguration = lazy(
    () => import("./features/pageConfiguration/PageConfiguration")
);

const AsyncPageRedirect = lazy(
    () => import("./features/deliveryRecovery/PageRedirect")
);

const AsyncDowntimeDetails = lazy(
    () => import("./features/deliveryOperations/DowntimeDetails")
);
const AsyncPowerBI = lazy(() => import("./features/powerBI/PowerBI"));
const AsyncBentoDemo = lazy(() => import("./features/bento/pages/demo/BentoDemo"));
const AsyncBentoCoaches = lazy(
    () => import("./features/bento/pages/coaches/BentoCoaches")
);
const AsyncBentoMarketplace = lazy(
    () => import("./features/bento/pages/marketplace/BentoMarketplace")
);
const AsyncBentoGuest360 = lazy(
    () => import("./features/bento/pages/guest360/BentoGuest360")
);
const AsyncBentoBackOfHouse = lazy(
    () => import("./features/bento/pages/back-of-house/BentoBackOfHouse")
);
const AsyncBentoPos = lazy(() => import("./features/bento/pages/pos/BentoPos"));
const AsyncBentoOrdering = lazy(
    () => import("./features/bento/pages/ordering/BentoOrdering")
);
const AsyncBentoLoyalty = lazy(
    () => import("./features/bento/pages/loyalty/BentoLoyalty")
);
const AsyncBentoReporting = lazy(
    () => import("./features/bento/pages/reporting/BentoReporting")
);
const AsyncBentoCoach = lazy(
    () => import("./features/bento/pages/coach/BentoCoach")
);
const AsyncBentoAcademy = lazy(
    () => import("./features/bento/pages/academy/BentoAcademy")
);
const AsyncBentoChecklist = lazy(
    () => import("./features/bento/pages/checklist/BentoChecklist")
);
const AsyncBentoCampaignEditor = lazy(
    () => import("./features/bento/pages/campaign-editor/BentoCampaignEditor")
);
const AsyncBentoLaborSchedule = lazy(
    () => import("./features/bento/pages/labor-schedule/BentoLaborSchedule")
);
const AsyncBentoVoidRefundReport = lazy(
    () => import("./features/bento/pages/void-refund-report/BentoVoidRefundReport")
);
const AsyncLoginPage = lazy(
    () => import("./features/authentication/components/LoginPage")
);

interface IProps {
    history: History;
}

interface IReduxProps {
    rootGroupId: string;
    legacySiteBaseUrl: string;
    clientCode: string;
    username: string;
    bannerMessage: string;
    hasCoachAssistant: boolean;
}

interface IState {
    groupIdSetupInvalidLocations: IRootGroupInvalidLocations;
    isChatVisible: boolean;
    isBentoDemoPage: boolean;
    isBentoShellPage: boolean;
    hasError: boolean;
}

type AppProps = IReduxProps & IProps & typeof actionCreators;

class App extends React.Component<AppProps, IState> {
    handleScreenResizeThrottled: DebouncedFunc<() => void>;
    chatRef: React.RefObject<HTMLDivElement>;
    unlistenHistory?: () => void;

    private isBentoDemoPath = (pathname: string): boolean =>
        pathname === "/" ||
        pathname.startsWith("/par-intelligence") ||
        pathname.startsWith("/dashboard/bento") ||
        pathname.startsWith("/login");

    // Bento pages that use the persistent sidebar/header shell. Excludes /login,
    // which is a full-screen Bento-styled page without the dashboard chrome.
    private isBentoShellPath = (pathname: string): boolean =>
        this.isBentoDemoPath(pathname) && !pathname.startsWith("/login");

    private applySemanticScope = (pathname: string): void => {
        const enabled = !this.isBentoDemoPath(pathname);
        document.documentElement.classList.toggle("semantic-enabled", enabled);
        document.body.classList.toggle("semantic-enabled", enabled);
    };

    constructor(props: AppProps) {
        super(props);

        this.applySemanticScope(props.history.location.pathname);

        this.state = {
            groupIdSetupInvalidLocations: {groupName: "", invalidLocations: []},
            isChatVisible: false,
            isBentoDemoPage: this.isBentoDemoPath(props.history.location.pathname),
            isBentoShellPage: this.isBentoShellPath(props.history.location.pathname),
            hasError: false
        };

        this.chatRef = createRef();

        // Limit the number of resize events fired to once every 500ms to improve performance
        this.handleScreenResizeThrottled = throttle(this.handleScreenResize, 500);
    }

    public componentDidMount() {
        if (!this.props.legacySiteBaseUrl) {
            this.props.requestDomainSettings();
        }

        // This validates the legacy Warehouse1 group/location tree - not applicable to Bento/PAR Intelligence
        // demo pages, which use their own isolated ParIntelligenceDemo business-unit hierarchy.
        if (this.props.rootGroupId && !this.state.isBentoDemoPage) {
            configurationApi
                .checkForValidGroupTree(this.props.rootGroupId)
                .then((data) => {
                    if (data) {
                        this.setState({groupIdSetupInvalidLocations: data});
                    }
                });
        }

        // Add event listener for screen resize
        window.addEventListener("resize", this.handleScreenResizeThrottled);

        this.unlistenHistory = this.props.history.listen((location) => {
            this.applySemanticScope(location.pathname);
            this.setState({
                isBentoDemoPage: this.isBentoDemoPath(location.pathname),
                isBentoShellPage: this.isBentoShellPath(location.pathname)
            });
        });
    }

    public componentDidUpdate(previousProps: AppProps) {
        if (
            this.props.rootGroupId &&
            this.props.rootGroupId !== previousProps.rootGroupId &&
            !this.state.isBentoDemoPage
        ) {
            configurationApi
                .checkForValidGroupTree(this.props.rootGroupId)
                .then((data) => {
                    if (data) {
                        this.setState({groupIdSetupInvalidLocations: data});
                    }
                });
        }
    }

    static getDerivedStateFromError(): Partial<IState> {
        return {hasError: true};
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error("Error caught in App component:", error, info);
    }

    componentWillUnmount() {
        // Remove event listener for screen resize
        window.removeEventListener("resize", this.handleScreenResizeThrottled);
        this.unlistenHistory?.();
    }

    handleScreenResize = () => {
        this.props.handleScreenResize(window.innerWidth);
    };

    handleChatButtonClick = () => {
        this.setState(
            (prevState) => ({isChatVisible: !prevState.isChatVisible}),
            () => {
                if (this.state.isChatVisible) {
                    // Hotjar event so that we can filter just for the most precious engagements
                    hj("event", "coach_ai_chat_opened");
                    this.chatRef.current?.scrollIntoView({behavior: "smooth"});
                }
            }
        );
    };

    public render(): JSX.Element {
        if (this.state.hasError) {
            return (
                <div className="app-error-boundary" role="alert">
                    <h1>Something went wrong.</h1>
                    <p>
                        An unexpected error occurred. Please refresh the page to
                        continue.
                    </p>
                    <button type="button" onClick={() => window.location.reload()}>
                        Refresh
                    </button>
                </div>
            );
        }

        const askCoachAIButton = new URL(
            "@/coach-ai/src/assets/AskCoachAIButton.svg",
            import.meta.url
        ).href;

        const {isBentoDemoPage, isBentoShellPage} = this.state;

        // Here we can control what pages are accessible for Freemium users
        return (
            <Router history={this.props.history}>
                {!isBentoDemoPage && <DelagetHeader />}
                <div
                    className={`layout-wrapper ${
                        isBentoDemoPage ? "bento-demo-layout" : "dashboard"
                    }`}
                >
                    {!isBentoDemoPage && this.props.bannerMessage && (
                        <BannerMessage bannerMessage={this.props.bannerMessage} />
                    )}
                    {this.props.rootGroupId &&
                    this.state.groupIdSetupInvalidLocations?.invalidLocations
                        ?.length > 0 ? (
                        <GroupTreeValidationError
                            groupIdSetupInvalidLocations={
                                this.state.groupIdSetupInvalidLocations
                            }
                        />
                    ) : isBentoShellPage ? (
                        <BentoShell>
                            <React.Suspense
                                fallback={<BentoLoadingScreen variant="contained" />}
                            >
                                <Switch>
                                    <ProtectedRoute
                                        exact
                                        path="/"
                                        component={AsyncBentoDemo}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/coaches"
                                        component={AsyncBentoCoaches}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/marketplace"
                                        component={AsyncBentoMarketplace}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/guest360"
                                        component={AsyncBentoGuest360}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/backofhouse"
                                        component={AsyncBentoBackOfHouse}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/pos"
                                        component={AsyncBentoPos}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/ordering"
                                        component={AsyncBentoOrdering}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/loyalty"
                                        component={AsyncBentoLoyalty}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/reporting"
                                        component={AsyncBentoReporting}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/coach"
                                        component={AsyncBentoCoach}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/academy"
                                        component={AsyncBentoAcademy}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/checklist"
                                        component={AsyncBentoChecklist}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/campaign-editor"
                                        component={AsyncBentoCampaignEditor}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/labor-schedule"
                                        component={AsyncBentoLaborSchedule}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/void-refund-report"
                                        component={AsyncBentoVoidRefundReport}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/dashboard/bento/:conceptUrlName?/:page_PK?/:businessUnitId?/:date?"
                                        component={AsyncBentoDemo}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                    <ProtectedRoute
                                        exact
                                        path="/par-intelligence/:conceptUrlName?/:page_PK?/:businessUnitId?/:date?"
                                        component={AsyncBentoDemo}
                                        isFullVersionOnly={false}
                                        isAdminOnly={false}
                                        routeType={RouteType.BENTO}
                                    />
                                </Switch>
                            </React.Suspense>
                        </BentoShell>
                    ) : (
                        <React.Suspense
                            fallback={
                                isBentoDemoPage ? (
                                    <BentoLoadingScreen />
                                ) : (
                                    <LoadingMessage />
                                )
                            }
                        >
                            <Switch>
                                <Route
                                    exact
                                    path="/login"
                                    component={AsyncLoginPage}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/reports"
                                    component={AsyncPowerBI}
                                    isFullVersionOnly={true}
                                    isAdminOnly={false}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/recovery"
                                    component={AsyncPageRedirect}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.REDIRECT}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/deliveryOperations"
                                    component={AsyncPageRedirect}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.REDIRECT}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/deliveryDowntimeDetails"
                                    component={AsyncPageRedirect}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.REDIRECT}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/tasks"
                                    component={AsyncTasks}
                                    isFullVersionOnly={true}
                                    isAdminOnly={false}
                                    routeType={RouteType.TASKS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/settings"
                                    component={AsyncUserSettings}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.USER_SETTINGS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/admin"
                                    component={AsyncAdminSettings}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isAdminOrRecoveryAdminOnly={true}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/admin/:conceptUrlName/:metricDefinitionId"
                                    component={
                                        AsyncLocationMetricThresholdConfigurationContainer
                                    }
                                    isFullVersionOnly={false}
                                    isAdminOnly={true}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/addToHomeScreen"
                                    component={AsyncA2HS}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/:conceptUrlName/:businessUnitId/metricDetails/:page_PK/:date/:xtd/:businessUnitAggregation"
                                    component={AsyncMetricDetails}
                                    isFullVersionOnly={true}
                                    isAdminOnly={false}
                                    routeType={RouteType.METRIC_DETAILS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/tasks/:taskId"
                                    component={AsyncTaskDetails}
                                    isFullVersionOnly={true}
                                    isAdminOnly={false}
                                    routeType={RouteType.TASKS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/monitors/:conceptUrlName/search"
                                    component={AsyncMonitorSearch}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.MONITORS_SEARCH}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/monitors/:conceptUrlName/create/:monitorId?"
                                    component={AsyncCreateMonitor}
                                    isFullVersionOnly={false}
                                    isAdminOnly={true}
                                    routeType={RouteType.CREATE_MONITOR}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/monitors/:conceptUrlName/:businessUnitId/:monitorId/details/:daysToLookBack"
                                    component={AsyncMonitorDetails}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.MONITOR_DETAILS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/monitors/:conceptUrlName/:businessUnitId"
                                    component={AsyncMonitors}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={true}
                                    routeType={RouteType.MONITORS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/monitors"
                                    component={AsyncMonitors}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={true}
                                    routeType={RouteType.MONITORS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/HomeOffice/:page_PK/DeliveryProviderPaymentData/:conceptUrlName/:date"
                                    component={AsyncDeliveryProvider}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={false}
                                    routeType={RouteType.HOME_OFFICE}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/:conceptUrlName/:businessUnitId/HomeOffice/:page_PK/EmployeeRetention/:date/:xtd/:businessUnitAggregation"
                                    component={AsyncEmployeeRetention}
                                    isFullVersionOnly={true}
                                    isAdminOnly={false}
                                    isCoachDetect={false}
                                    routeType={RouteType.EMPLOYEE_RETENTION}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/DifferentialPay/:conceptUrlName/:businessUnitId"
                                    component={AsyncDifferentialPay}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={false}
                                    routeType={RouteType.DIFFERENTIAL_PAY}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/DifferentialPay"
                                    component={AsyncDifferentialPay}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={false}
                                    routeType={RouteType.DIFFERENTIAL_PAY}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/pageconfiguration/:page_PK?/:date"
                                    component={AsyncPageConfiguration}
                                    isFullVersionOnly={true}
                                    isAdminOnly={true}
                                    routeType={RouteType.PAGE_CONFIGURATION}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/DeliveryOperations/:page_PK/:conceptUrlName/:businessUnitId/:date/:businessUnitAggregation"
                                    component={AsyncDeliveryOperations}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isCoachDetect={false}
                                    isDeliveryOperations={true}
                                    routeType={RouteType.DELIVERY_OPERATIONS}
                                />
                                <ProtectedRoute
                                    exact
                                    path="/dashboard/DeliveryOperations/DowntimeDetails/:page_PK/:conceptUrlName/:businessUnitId/:date/:businessUnitAggregation"
                                    component={AsyncDowntimeDetails}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    isDeliveryOperations={true}
                                    routeType={RouteType.DELIVERY_OPERATIONS}
                                />
                                <Route
                                    exact
                                    path="/dashboard/theme"
                                    component={AsyncTheme}
                                />
                                {/* The path below is used so Coach (Dashboard) loads directly from the site root.
                                    However, for local development we need to keep /dashboard in the url.
                                */}
                                <ProtectedRoute
                                    exact
                                    path="/dashboard"
                                    component={Dashboard}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.DASHBOARD}
                                />
                                {/* Root "/" is now BentoDemo (declared at top of Switch).
                                    The path below previously handled the root; it is kept as a
                                    named fallback in case of direct /dashboard navigation. */}
                                <ProtectedRoute
                                    exact={false}
                                    path="/dashboard/:conceptUrlName?/:page_PK?/:businessUnitId?/:date?"
                                    component={Dashboard}
                                    isFullVersionOnly={false}
                                    isAdminOnly={false}
                                    routeType={RouteType.DASHBOARD}
                                />
                                {/* The main dashboard route goes last because of ambiguous matching,
                                it will first make sure that what comes after /dashboard/ isn't one
                                of our other routes, then it will try it as a business date param */}
                            </Switch>
                        </React.Suspense>
                    )}
                </div>
                {this.props.hasCoachAssistant && !isBentoDemoPage && (
                    <>
                        <div ref={this.chatRef}>
                            {this.state.isChatVisible && <Chat />}
                        </div>
                        <button
                            onClick={this.handleChatButtonClick}
                            style={{
                                position: "fixed",
                                bottom: "20px",
                                right: "20px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                zIndex: 1000
                            }}
                        >
                            <img
                                src={askCoachAIButton}
                                alt="Ask CoachAI"
                                style={{
                                    // Original dimensions are 276x108, scaled down to 160x63 to fit better
                                    width: "160px",
                                    height: "63px"
                                }}
                            />
                        </button>
                    </>
                )}
                {ENV.LIBRARY_MODE || isBentoDemoPage ? <> </> : <DelagetFooter />}
                {!isBentoDemoPage && (
                    <>
                        <FreemiumMarketingModal />
                        <GlobalTicketModalManager />
                    </>
                )}
            </Router>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    rootGroupId: state.application.selectedClientConcept.rootGroupId,
    legacySiteBaseUrl: state.application.legacySiteBaseUrl,
    clientCode: state.application.userInfo.clientCode,
    username: state.application.userInfo.userName,
    bannerMessage: state.application.bannerMessage,
    hasCoachAssistant: state.application.userInfo.hasCoachAssistant
});

export default connect(mapStateToProps, actionCreators, (state, actions, props) => ({
    ...state,
    ...actions,
    ...props
}))(App);
